/**
 * Centrale logica voor de "Orders Decoprof" feature.
 * Alle exclude-lijsten, type-mappings en pakbon-URL helpers staan hier,
 * zodat aanpassingen geïsoleerd zijn.
 *
 * NB: toegangscontrole loopt via het standaard role_permissions-systeem
 * (Resource = "ordersDecoprof") en NIET meer via een per-user feature flag.
 * Zie src/hooks/rollenbeheer/useRolePermissions.ts → hasPermission("ordersDecoprof", "read").
 */

import { toast } from "sonner";

// ----- Filter: welke leveranciers zijn UITGESLOTEN ---------------------------

/**
 * Leveranciers die expliciet NIET als "Decoprof-order" tellen.
 * Een order wordt getoond zodra minstens één regel een leverancier
 * heeft die hier NIET in voorkomt. Nieuwe leveranciers verschijnen
 * dus automatisch.
 */
export const ORDERS_DECOPROF_EXCLUDED_SUPPLIERS: ReadonlySet<string> = new Set([
  "Luijten-VVZ BV",
  "Delta Paints BV",
]);

export function isExcludedSupplier(supplier: string | null | undefined): boolean {
  if (!supplier) return false;
  return ORDERS_DECOPROF_EXCLUDED_SUPPLIERS.has(supplier);
}

// ----- Type-mapping (leverancier -> "Type order") ---------------------------

export type OrderDecoprofType =
  | "Verfbestelling"
  | "Behangorder"
  | "Spuitbusbestelling"
  | "Overige";

/**
 * Bekende leveranciers — altijd zichtbaar in de Leverancier-filter dropdown,
 * ook als er momenteel 0 orders voor zijn. Houd in sync met SUPPLIER_TO_TYPE.
 */
export const KNOWN_SUPPLIERS: ReadonlyArray<string> = [
  "Akzo Nobel Decorative Coatings",
  "Arte International",
  "Baten Trading Company",
  "Decoprof bv",
  "Dutch Walltextile Company",
  "Eijffinger",
  "Intervos",
  "Mooij Verf BV",
  "Nature at Home",
  "Sikkens Center en Simonis",
];

const SUPPLIER_TO_TYPE: Record<string, OrderDecoprofType> = {
  "Akzo Nobel Decorative Coatings": "Verfbestelling",
  "Arte International": "Behangorder",
  "Baten Trading Company": "Spuitbusbestelling",
  "Decoprof bv": "Overige",
  "Dutch Walltextile Company": "Behangorder",
  "Eijffinger": "Behangorder",
  "Intervos": "Behangorder",
  "Mooij Verf BV": "Verfbestelling",
  "Nature at Home": "Behangorder",
  "Sikkens Center en Simonis": "Verfbestelling",
};

export function mapSupplierToType(
  supplier: string | null | undefined,
): OrderDecoprofType {
  if (!supplier) return "Overige";
  return SUPPLIER_TO_TYPE[supplier] ?? "Overige";
}

/**
 * R57 — FIX 4: alle unieke types voor een order, gebaseerd op ALLE
 * non-excluded suppliers. Mixed orders (bv. Mooij Verf + Intervos + Akzo)
 * geven 2-3 verschillende type-badges. Alfabetisch gesorteerd voor
 * deterministische rendering.
 */
/**
 * R73 — alle unieke leveranciers van een order, ge-excluded suppliers eruit
 * gefilterd. Alfabetisch (NL) gesorteerd voor deterministische rendering.
 * Hergebruikt dezelfde aanpak als getOrderTypes (R57).
 */
export function getOrderSuppliers(suppliers: ReadonlyArray<string>): string[] {
  const set = new Set<string>();
  for (const s of suppliers) {
    if (!s) continue;
    if (ORDERS_DECOPROF_EXCLUDED_SUPPLIERS.has(s)) continue;
    set.add(s);
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b, "nl"));
}

export function getOrderTypes(suppliers: ReadonlyArray<string>): OrderDecoprofType[] {
  const types = new Set<OrderDecoprofType>();
  for (const s of suppliers) {
    if (!s) continue;
    if (ORDERS_DECOPROF_EXCLUDED_SUPPLIERS.has(s)) continue;
    types.add(mapSupplierToType(s));
  }
  if (types.size === 0) types.add("Overige");
  // R74: filter "Overige" weg zodra er een specifieker type aanwezig is.
  if (types.size > 1 && types.has("Overige")) types.delete("Overige");
  return Array.from(types).sort((a, b) => a.localeCompare(b, "nl"));
}

export const ORDER_DECOPROF_TYPE_OPTIONS: ReadonlyArray<OrderDecoprofType> = [
  "Verfbestelling",
  "Behangorder",
  "Spuitbusbestelling",
  "Overige",
];

// ----- Status ---------------------------------------------------------------

export type OrderDecoprofStatus =
  | "Nieuw"
  | "Uitgeprint"
  | "Aangemaakt"
  | "Nalevering winkel"
  | "Behangorder besteld"
  | "Afwijkende verzenddatum"
  | "In afwachting van klant"
  | "Afgerond";

export const ORDER_DECOPROF_STATUSES: ReadonlyArray<OrderDecoprofStatus> = [
  "Nieuw",
  "Uitgeprint",
  "Aangemaakt",
  "Nalevering winkel",
  "Behangorder besteld",
  "Afwijkende verzenddatum",
  "In afwachting van klant",
  "Afgerond",
];

// Rangorde: hoger = verder in workflow. De 4 nieuwe statussen krijgen rank 3
// zodat handmatige status niet wordt teruggezet door latere sync met default "Nieuw".
// "Afgerond" is de eindstatus (rank 4).
const STATUS_RANK: Record<OrderDecoprofStatus, number> = {
  Nieuw: 0,
  Uitgeprint: 1,
  Aangemaakt: 2,
  "Nalevering winkel": 3,
  "Behangorder besteld": 3,
  "Afwijkende verzenddatum": 3,
  "In afwachting van klant": 3,
  "Afgerond": 4,
};

export const maxOrderDecoprofStatus = (
  a: OrderDecoprofStatus | null | undefined,
  b: OrderDecoprofStatus | null | undefined,
): OrderDecoprofStatus => {
  const av = a && a in STATUS_RANK ? STATUS_RANK[a] : -1;
  const bv = b && b in STATUS_RANK ? STATUS_RANK[b] : -1;
  if (av < 0 && bv < 0) return "Nieuw";
  return av >= bv ? (a as OrderDecoprofStatus) : (b as OrderDecoprofStatus);
};

// ----- Shipment-filter: "afhalen in winkel" --------------------------------
// FASE 2 punt 1 — orders met deze shipment_title-waardes worden uitgesloten,
// tenzij customer_number in ALLOW_CUSTOMER_NUMBERS staat (punt 2).
export const ORDERS_DECOPROF_PICKUP_SHIPMENT_TITLES: ReadonlySet<string> = new Set(
  [
    "Ophalen bij Decoprof - Ridderkerk",
    "Ophalen bij Decoprof - Dordrecht",
    "Ophalen bij Decoprof - 's Gravendeel",
    // NB: "Ophalen bij PostNL locatie" is GEEN afhalen-in-winkel, maar een
    // PostNL-verzending naar een afhaalpunt → telt als normale verzendorder.
  ].map((t) => t.trim()),
);

/** Klanten die de afhaal-uitsluiting overslaan. Mark's uitzondering 21-mei. */
export const ORDERS_DECOPROF_ALLOW_CUSTOMER_NUMBERS: ReadonlySet<string> = new Set([
  "201393467", // Jacco Goud
]);

export function isPickupShipment(title: string | null | undefined): boolean {
  if (!title) return false;
  return ORDERS_DECOPROF_PICKUP_SHIPMENT_TITLES.has(title.trim());
}

export function isAllowedPickupCustomer(customerNumber: string | null | undefined): boolean {
  if (!customerNumber) return false;
  return ORDERS_DECOPROF_ALLOW_CUSTOMER_NUMBERS.has(customerNumber.trim());
}

// ----- Kleurnummer parser (uit Lightspeed custom_fields) -------------------
export function parseKleurnummer(custom_fields: unknown): string {
  if (!Array.isArray(custom_fields)) return "";
  for (const f of custom_fields as any[]) {
    const title = String(f?.title ?? "").trim().toLowerCase();
    if (title !== "kleurnummer") continue;
    const values = Array.isArray(f?.values) ? f.values : [];
    for (const v of values) {
      const raw = v?.value;
      if (raw === false || raw === null || raw === undefined) continue;
      const full = String(raw).trim();
      if (!full) continue;
      return full.split(" |")[0].trim();
    }
  }
  return "";
}

// ----- Pakbon: server-side gegenereerde PDF via edge function ---------------
// BUILD=R27-merged-pdf-batch

/**
 * R45: iOS-safe PDF delivery voor batch-pakbon.
 *
 * Probleem op iOS Safari:
 *   - <a download> met blob: URL wordt vaak genegeerd of opent als gewone link.
 *   - window.open(blobUrl) NA een await wordt geblokkeerd (popup-block buiten user-gesture).
 *
 * Oplossing:
 *   - De caller MOET in dezelfde click-handler `window.open('about:blank','_blank')`
 *     synchroon openen en het Window-object via `preOpenedWindow` doorgeven.
 *   - Na fetch zetten we `preOpenedWindow.location.href = blobUrl` → werkt op iOS,
 *     omdat het bestaande window al door de gebruiker geopend is.
 *   - Op desktop blijft <a download> werken; we gebruiken het pre-opened window
 *     alleen wanneer beschikbaar.
 */
export type BatchPrintResult = {
  succeededOrderids: number[];
  failedOrderids: number[];
  failedReasons: Record<string, string>;
};

export type BatchPrintProgress = {
  /** 1-based volgnummer van de chunk. */
  chunkIndex: number;
  chunkCount: number;
  /** Orderids die in deze chunk zaten. */
  chunkOrderids: number[];
  /** Aantal orders dat na deze chunk verwerkt is (gelukt + mislukt). */
  processed: number;
  total: number;
  result: BatchPrintResult;
  /** Foutmelding wanneer de hele chunk faalde (na alle retries). */
  error?: string;
};

export type BatchPrintOptions = {
  onChunkDone?: (progress: BatchPrintProgress) => void | Promise<void>;
};

/** Server rendert serieel + Lightspeed rate-limit → kleine chunks, nooit parallel. */
const BATCH_PRINT_CHUNK_SIZE = 3;
/** Netwerkfout / preflight-502 → dezelfde chunk opnieuw (nooit bij AbortError). */
const BATCH_PRINT_MAX_RETRIES = 2;
const BATCH_PRINT_RETRY_DELAY_MS = 1000;

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

const pdfBlob = (bytes: Uint8Array): Blob =>
  new Blob([bytes as unknown as BlobPart], { type: "application/pdf" });

/** Levert een PDF af: mobiel via pre-opened tab, anders via <a download>. */
function deliverPdf(
  blob: Blob,
  fileName: string,
  preOpenedWindow: Window | null | undefined,
) {
  const blobUrl = URL.createObjectURL(blob);
  const cleanup = () => setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);

  // R45: iOS Safari → gebruik pre-opened window (blijft binnen user-gesture).
  if (isMobileDevice() && preOpenedWindow && !preOpenedWindow.closed) {
    console.log(`[R45 batch-print] iOS-pad: navigeer pre-opened window naar blob (${blob.size} bytes)`);
    try {
      preOpenedWindow.location.href = blobUrl;
      cleanup();
      return;
    } catch (e) {
      console.warn("[R45 batch-print] pre-opened window navigatie faalde, fallback naar <a download>", e);
    }
  }

  // Desktop / fallback: native download via <a download>.
  if (preOpenedWindow) try { preOpenedWindow.close(); } catch { /* noop */ }
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = fileName;
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    try { document.body.removeChild(a); } catch { /* ignore */ }
    cleanup();
  }, 0);
}

/** Voegt de deel-PDF's van alle chunks samen tot één document. */
async function mergePdfBytes(parts: Uint8Array[]): Promise<Uint8Array> {
  const { PDFDocument } = await import("pdf-lib");
  const merged = await PDFDocument.create();
  for (const bytes of parts) {
    const src = await PDFDocument.load(bytes, { ignoreEncryption: true, updateMetadata: false });
    const pages = await merged.copyPages(src, src.getPageIndices());
    pages.forEach((p) => merged.addPage(p));
  }
  return await merged.save({ useObjectStreams: false });
}

type ChunkOutcome = BatchPrintResult & {
  /** PDF-bytes van deze chunk; leeg wanneer de server niets bruikbaars gaf. */
  bytes: Uint8Array | null;
  fileName: string;
};

/**
 * Eén HTTP-request naar de batch-edge-function (max BATCH_PRINT_CHUNK_SIZE orders).
 * Server cap (50) en de 90s AbortController per request blijven ongewijzigd.
 * Levert de PDF-bytes terug; de aflevering gebeurt pas ná alle chunks.
 */
async function printPackingSlipsChunk(
  orderids: number[],
): Promise<ChunkOutcome> {
  const sessionToken =
    typeof window !== "undefined" ? localStorage.getItem("auth_session_token") : null;
  if (!sessionToken) {
    throw new Error("Niet ingelogd (geen sessie token)");
  }

  const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
  const url = `https://${projectId}.supabase.co/functions/v1/print-decoprof-packing-slips-batch`;

  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "(no-ua)";
  console.log(`[R45 batch-print] fetch — sending N=${orderids.length} ids=[${orderids.join(",")}] ua="${ua}"`);
  const ac = new AbortController();
  const timeoutId = setTimeout(() => ac.abort(), 90_000);
  console.time(`[batch-print] fetch ${orderids.length} orders`);
  let res: Response;
  try {
    res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-session-token": sessionToken,
        apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      },
      body: JSON.stringify({ orderids }),
      signal: ac.signal,
    });
  } catch (e: any) {
    if (e?.name === "AbortError") {
      const err = new Error("Pakbon-batch duurde te lang (>90s) — probeer minder orders tegelijk");
      (err as any).name = "AbortError";
      (err as any).noRetry = true;
      throw err;
    }
    throw e;
  } finally {
    clearTimeout(timeoutId);
    console.timeEnd(`[batch-print] fetch ${orderids.length} orders`);
  }

  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    let failedReasons: Record<string, string> = {};
    try {
      const j = await res.json();
      msg = j?.error || msg;
      if (j?.failedReasons && typeof j.failedReasons === "object") {
        failedReasons = j.failedReasons;
      }
    } catch { /* ignore */ }
    const err = new Error(msg);
    (err as any).failedReasons = failedReasons;
    (err as any).httpStatus = res.status;
    throw err;
  }

  const succeededHeader = res.headers.get("x-succeeded-orderids") || "";
  const failedHeader = res.headers.get("x-failed-orderids") || "";
  const reasonsHeader = res.headers.get("x-failed-reasons") || "";
  const parseCsv = (s: string) =>
    s.split(",").map((x) => Number(x.trim())).filter((n) => Number.isFinite(n) && n > 0);
  const succeededOrderids = parseCsv(succeededHeader);
  const failedOrderids = parseCsv(failedHeader);
  let failedReasons: Record<string, string> = {};
  if (reasonsHeader) {
    try { failedReasons = JSON.parse(reasonsHeader); } catch { /* ignore */ }
  }

  const blob = await res.blob();
  if (blob.type && !blob.type.includes("pdf")) {
    throw new Error("Onverwacht response-type van pakbon-batch-functie");
  }

  const cd = res.headers.get("content-disposition") || "";
  const match = /filename="?([^"]+)"?/i.exec(cd);
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  const fallbackName =
    `pakbonnen-decoprof-${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}` +
    `-${pad(now.getHours())}${pad(now.getMinutes())}.pdf`;
  const fileName = match?.[1] || fallbackName;

  const bytes = new Uint8Array(await blob.arrayBuffer());

  // Validatie: pas als de client een échte PDF heeft ontvangen mag de chunk
  // als geslaagd tellen (invariant: geen 'Uitgeprint' zonder pakbon).
  if (bytes.length <= 1024) {
    throw new Error(`Ontvangen PDF te klein (${bytes.length} bytes)`);
  }
  const header = String.fromCharCode(...bytes.slice(0, 5));
  if (header !== "%PDF-") {
    throw new Error("Ontvangen bestand is geen geldige PDF");
  }

  return { succeededOrderids, failedOrderids, failedReasons, bytes, fileName };
}

/**
 * Bevestigt aan de server dat de PDF is aangekomen; pas dan wordt de status
 * op 'Uitgeprint' gezet. Levert het aantal bijgewerkte rijen.
 */
async function confirmPrinted(orderids: number[]): Promise<number> {
  const sessionToken =
    typeof window !== "undefined" ? localStorage.getItem("auth_session_token") : null;
  if (!sessionToken) throw new Error("Niet ingelogd (geen sessie token)");

  const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
  const url = `https://${projectId}.supabase.co/functions/v1/print-decoprof-packing-slips-batch`;

  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-session-token": sessionToken,
      apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
    },
    body: JSON.stringify({ action: "confirm", orderids }),
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try { const j = await res.json(); msg = j?.error || msg; } catch { /* ignore */ }
    throw new Error(msg);
  }
  const j = await res.json().catch(() => ({}));
  return Number(j?.updated ?? 0);
}



/**
 * Bulk-print: splitst client-side in chunks van max 5 orders en verstuurt die
 * STRIKT NA ELKAAR. Per chunk max 2 extra pogingen bij een TypeError
 * (netwerkfout / CORS-preflight-502); een AbortError (90s) wordt niet herhaald.
 * Een falende chunk stopt de reeks niet — die orders komen in failedOrderids.
 */
export async function printPackingSlipsBatch(
  orderids: number[],
  preOpenedWindow?: Window | null,
  options?: BatchPrintOptions,
): Promise<BatchPrintResult> {
  const sessionToken =
    typeof window !== "undefined" ? localStorage.getItem("auth_session_token") : null;
  if (!sessionToken) {
    if (preOpenedWindow) try { preOpenedWindow.close(); } catch { /* noop */ }
    throw new Error("Niet ingelogd (geen sessie token)");
  }
  if (orderids.length === 0) {
    if (preOpenedWindow) try { preOpenedWindow.close(); } catch { /* noop */ }
    throw new Error("Geen orders geselecteerd");
  }

  const chunks: number[][] = [];
  for (let i = 0; i < orderids.length; i += BATCH_PRINT_CHUNK_SIZE) {
    chunks.push(orderids.slice(i, i + BATCH_PRINT_CHUNK_SIZE));
  }

  const succeededOrderids: number[] = [];
  const failedOrderids: number[] = [];
  const failedReasons: Record<string, string> = {};
  const confirmFailedOrderids: number[] = [];
  const parts: { bytes: Uint8Array; fileName: string }[] = [];

  let processed = 0;
  let firstError: any = null;

  // Mobiel: het pre-opened tabblad blijft leeg tot alle chunks binnen zijn.
  if (preOpenedWindow && !preOpenedWindow.closed) {
    try {
      preOpenedWindow.document.write(
        "<title>Pakbonnen</title><body style=\"font:16px system-ui;padding:24px\">Pakbonnen worden gemaakt…</body>",
      );
    } catch { /* noop */ }
  }

  for (let c = 0; c < chunks.length; c++) {
    const chunk = chunks[c];

    let chunkResult: ChunkOutcome | null = null;
    let chunkError: any = null;

    for (let attempt = 0; attempt <= BATCH_PRINT_MAX_RETRIES; attempt++) {
      try {
        chunkResult = await printPackingSlipsChunk(chunk);
        chunkError = null;
        break;
      } catch (e: any) {
        chunkError = e;
        const isNetworkError = e instanceof TypeError || e?.name === "TypeError";
        const isServerError = typeof e?.httpStatus === "number" && e.httpStatus >= 500;
        const retryable =
          (isNetworkError || isServerError) && !e?.noRetry && attempt < BATCH_PRINT_MAX_RETRIES;
        if (!retryable) break;
        console.warn(
          `[batch-print] chunk ${c + 1}/${chunks.length} tijdelijke fout (poging ${attempt + 1}) — opnieuw over ${BATCH_PRINT_RETRY_DELAY_MS}ms`,
          e,
        );
        await sleep(BATCH_PRINT_RETRY_DELAY_MS);
      }
    }

    let confirmOk = true;
    if (chunkResult) {
      succeededOrderids.push(...chunkResult.succeededOrderids);
      failedOrderids.push(...chunkResult.failedOrderids);
      Object.assign(failedReasons, chunkResult.failedReasons);
      if (chunkResult.bytes && chunkResult.bytes.length > 0) {
        parts.push({ bytes: chunkResult.bytes, fileName: chunkResult.fileName });
      }
      // Status pas zetten NA aantoonbaar geleverde PDF.
      if (chunkResult.succeededOrderids.length > 0) {
        try {
          const updated = await confirmPrinted(chunkResult.succeededOrderids);
          console.log(`[batch-print] confirm chunk ${c + 1}/${chunks.length} → ${updated} rij(en) Uitgeprint`);
        } catch (e) {
          confirmOk = false;
          confirmFailedOrderids.push(...chunkResult.succeededOrderids);
          console.error(`[batch-print] confirm chunk ${c + 1}/${chunks.length} mislukt`, e);
        }
      }
    } else {
      const msg = chunkError?.message ?? String(chunkError);
      if (!firstError) firstError = chunkError;
      failedOrderids.push(...chunk);
      chunk.forEach((id) => { failedReasons[String(id)] = msg; });
      if (chunkError?.failedReasons && typeof chunkError.failedReasons === "object") {
        Object.assign(failedReasons, chunkError.failedReasons);
      }
      console.error(`[batch-print] chunk ${c + 1}/${chunks.length} definitief mislukt`, chunkError);
    }

    processed += chunk.length;
    if (options?.onChunkDone && confirmOk) {
      try {
        await options.onChunkDone({
          chunkIndex: c + 1,
          chunkCount: chunks.length,
          chunkOrderids: chunk,
          processed,
          total: orderids.length,
          result: chunkResult
            ? {
                succeededOrderids: chunkResult.succeededOrderids,
                failedOrderids: chunkResult.failedOrderids,
                failedReasons: chunkResult.failedReasons,
              }
            : { succeededOrderids: [], failedOrderids: [...chunk], failedReasons: {} },
          error: chunkResult ? undefined : (chunkError?.message ?? String(chunkError)),
        });
      } catch (e) {
        console.warn("[batch-print] onChunkDone handler faalde", e);
      }
    }

  }

  // Alle chunk-PDF's samenvoegen tot ÉÉN bestand en één keer afleveren.
  if (parts.length > 0) {
    const fileName = parts[0].fileName;
    if (parts.length === 1) {
      deliverPdf(pdfBlob(parts[0].bytes), fileName, preOpenedWindow);
    } else {
      try {
        const mergedBytes = await mergePdfBytes(parts.map((p) => p.bytes));
        console.log(`[batch-print] merged ${parts.length} chunk-PDFs → ${mergedBytes.length} bytes`);
        deliverPdf(pdfBlob(mergedBytes), fileName, preOpenedWindow);
      } catch (e) {
        // Vangnet: losse chunk-PDF's alsnog één voor één downloaden.
        console.error("[batch-print] samenvoegen mislukt — losse downloads als fallback", e);
        parts.forEach((p, i) => {
          deliverPdf(
            pdfBlob(p.bytes),
            `${p.fileName.replace(/\.pdf$/i, "")}-${i + 1}.pdf`,
            i === 0 ? preOpenedWindow : null,
          );
        });
      }
    }
  } else if (preOpenedWindow) {
    try { preOpenedWindow.close(); } catch { /* noop */ }
  }

  // Alles mislukt → gooi door zodat de bestaande error-afhandeling blijft werken.
  if (succeededOrderids.length === 0 && firstError) {
    if (!(firstError as any).failedReasons) (firstError as any).failedReasons = failedReasons;
    throw firstError;
  }

  // PDF wél geleverd, status niet bijgewerkt → één duidelijke waarschuwing.
  if (confirmFailedOrderids.length > 0) {
    toast.warning(
      `PDF geleverd maar status niet bijgewerkt voor ${confirmFailedOrderids.length} orders`,
    );
  }

  return { succeededOrderids, failedOrderids, failedReasons };

}




export function isMobileDevice(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
}

